/**
 * Top bar: menu (phones), tenant, the parcel search box (press / anywhere; any identifier),
 * scan shortcut, site switcher, notifications, theme, user menu.
 */
import { Bell, Building2, Check, CheckCheck, KeyRound, LogOut, Menu, Monitor, Moon, ScanLine, Search, Sun, UserRound, Warehouse } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DateText, IconButton, MenuItem, Popover, SEARCH_INPUT_ID, SearchBox, cx, useToast } from '../../components/ui';
import { useAuth } from '../../lib/auth';
import { P } from '../../lib/permissions';
import { useNotifications } from '../../lib/queries';
import { useCurrentSite } from '../../lib/site';
import { useTheme } from '../../lib/theme';

export function TopBar({ onMenu }: { onMenu: () => void }) {
  const { can, user } = useAuth();
  const navigate = useNavigate();
  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-1 border-b border-line bg-card/95 px-2 backdrop-blur sm:gap-2 sm:px-4">
      <IconButton label="Open menu" onClick={onMenu} className="lg:hidden">
        <Menu aria-hidden className="h-5 w-5" />
      </IconButton>
      <OrgSwitcher />
      {user?.org && can(P.parcelsView) && (
        <>
          <GlobalSearch />
          <IconButton label="Find a parcel" className="md:hidden" onClick={() => navigate('/parcels')}>
            <Search aria-hidden className="h-5 w-5" />
          </IconButton>
        </>
      )}
      <div className="ml-auto flex items-center gap-0.5">
        {user?.org && can(P.parcelsView) && (
          <Link to="/parcels/scan" className="mr-1 hidden min-h-touch items-center gap-2 rounded-tag bg-brand px-3.5 text-sm font-semibold text-brand-ink hover:bg-brand/90 sm:inline-flex" aria-label="Scan and find">
            <ScanLine aria-hidden className="h-4 w-4" />
            <span className="hidden lg:inline">Scan</span>
          </Link>
        )}
        {user?.org && <SiteSwitcher />}
        <NotificationsBell />
        <ThemeToggle />
        <UserMenu />
      </div>
    </header>
  );
}

function OrgSwitcher() {
  const { user, switchOrg } = useAuth();
  const toast = useToast();
  if (!user) return null;
  const name = user.org?.name ?? (user.platformAdmin ? 'ITCSI platform' : 'No company');
  const label = (
    <>
      <Building2 aria-hidden className="h-4 w-4 shrink-0 text-muted" />
      <span className="truncate">{name}</span>
    </>
  );
  // Platform admins switch between the platform and organisations with a live support session.
  if (user.platformAdmin ? user.orgs.length === 0 : user.orgs.length <= 1) return <span className="hidden min-h-touch min-w-0 max-w-[220px] items-center gap-2 px-2 text-sm font-semibold sm:inline-flex">{label}</span>;
  return (
    <Popover
      label="Company"
      trigger={({ open, toggle, id }) => (
        <button type="button" aria-haspopup="menu" aria-expanded={open} aria-controls={id} onClick={toggle} className="inline-flex min-h-touch min-w-0 max-w-[42vw] items-center gap-2 rounded-tag border border-line px-3 text-sm font-semibold hover:bg-sunken sm:max-w-[240px]">
          {label}
        </button>
      )}
    >
      {(close) => (
        <div className="max-h-[60vh] w-72 overflow-auto">
          <p className="px-2.5 pb-1 pt-1.5 text-xs text-muted">Switch company. Each one’s parcels stay separate.</p>
          {user.orgs.map((o) => (
            <MenuItem
              key={o.id}
              icon={user.org?.id === o.id ? <Check className="text-brand" /> : <span className="block h-4 w-4" />}
              onClick={() => {
                close();
                if (o.id !== user.org?.id) void switchOrg(o.id).catch(() => toast.error('Could not switch company. Try again.'));
              }}
            >
              {o.name}
            </MenuItem>
          ))}
          {user.platformAdmin && (
            <MenuItem
              icon={!user.org ? <Check className="text-brand" /> : <span className="block h-4 w-4" />}
              onClick={() => {
                close();
                if (user.org) void switchOrg(null).catch(() => toast.error('Could not switch back to the platform. Try again.'));
              }}
            >
              ITCSI platform
            </MenuItem>
          )}
        </div>
      )}
    </Popover>
  );
}

/** Find a parcel by any identifier (Parcel ID, waybill, client ref, EPC, SSCC). Press / anywhere. */
function GlobalSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  return (
    <SearchBox
      id={SEARCH_INPUT_ID}
      value={q}
      onChange={setQ}
      shortcutHint
      placeholder="Parcel ID, waybill, reference or tag"
      className="hidden flex-1 md:block md:max-w-xl"
      onSubmit={(term) => {
        setQ('');
        navigate(`/parcels?q=${encodeURIComponent(term)}`);
      }}
    />
  );
}

/** The site the user works at (default filter and scan site). Hidden when there is only one. */
function SiteSwitcher() {
  const { siteId, site, sites, setSiteId } = useCurrentSite();
  if (sites.length <= 1) return site ? <span className="hidden min-h-touch items-center gap-1.5 px-2 text-sm font-semibold text-muted sm:inline-flex"><Warehouse aria-hidden className="h-4 w-4" />{site.code}</span> : null;
  return (
    <Popover
      align="right"
      label="Site"
      trigger={({ open, toggle, id }) => (
        <button type="button" aria-haspopup="menu" aria-expanded={open} aria-controls={id} onClick={toggle} className="inline-flex min-h-touch items-center gap-1.5 rounded-tag px-2.5 text-sm font-semibold hover:bg-sunken">
          <Warehouse aria-hidden className="h-4 w-4 text-muted" />
          <span>{site?.code ?? 'All sites'}</span>
        </button>
      )}
    >
      {(close) => (
        <div className="w-64">
          <p className="px-2.5 pb-1 pt-1.5 text-xs text-muted">The site you work at. Lists and scans start here.</p>
          <MenuItem icon={!siteId ? <Check className="text-brand" /> : <span className="block h-4 w-4" />} onClick={() => { close(); setSiteId(null); }}>All my sites</MenuItem>
          {sites.map((x) => (
            <MenuItem key={x.id} icon={siteId === x.id ? <Check className="text-brand" /> : <span className="block h-4 w-4" />} onClick={() => { close(); setSiteId(x.id); }}>
              {x.code} · {x.name}
            </MenuItem>
          ))}
        </div>
      )}
    </Popover>
  );
}

function NotificationsBell() {
  const n = useNotifications();
  const list = n.data ?? [];
  const unread = list.filter((x) => !x.read).length;
  return (
    <Popover
      align="right"
      label="Notifications"
      trigger={({ open, toggle, id }) => (
        <IconButton label={unread ? `${unread} unread notifications` : 'Notifications'} aria-haspopup="menu" aria-expanded={open} aria-controls={id} onClick={toggle} badge={unread}>
          <Bell aria-hidden className="h-5 w-5" />
        </IconButton>
      )}
    >
      {(close) => (
        <div className="w-80">
          <p className="px-2.5 pb-1 pt-2 text-sm font-semibold">Notifications</p>
          {list.length === 0 ? (
            <p className="px-2.5 pb-3 text-sm text-muted">{n.isError ? 'Notifications are not available yet.' : 'Nothing new. Batches, approvals and alerts show up here.'}</p>
          ) : (
            <ul className="ledger max-h-80 overflow-auto">
              {list.map((a) => (
                <li key={a.id}>
                  <Link to={a.href ?? '/notifications'} onClick={close} className="flex gap-2.5 px-2.5 py-2.5 hover:bg-sunken">
                    <span aria-hidden className={cx('mt-1.5 h-2 w-2 shrink-0 rounded-full', a.read ? 'bg-transparent' : 'bg-brand')} />
                    <span className="min-w-0">
                      <span className={cx('block text-sm', !a.read && 'font-semibold')}>
                        {a.title}
                        {!a.read && <span className="sr-only"> (unread)</span>}
                      </span>
                      <span className="block text-xs text-muted">
                        <DateText value={a.createdAt} mode="ago" />
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
          <div className="border-t border-line p-1">
            <MenuItem icon={<CheckCheck />} href="/notifications">
              See all notifications
            </MenuItem>
          </div>
        </div>
      )}
    </Popover>
  );
}

function ThemeToggle() {
  const { choice } = useTheme();
  const Icon = choice === 'light' ? Sun : choice === 'dark' ? Moon : Monitor;
  return (
    <Popover
      align="right"
      label="Theme"
      trigger={({ open, toggle, id }) => (
        <IconButton label={`Theme: ${choice === 'system' ? 'match device' : choice}`} aria-haspopup="menu" aria-expanded={open} aria-controls={id} onClick={toggle} className="hidden sm:grid">
          <Icon aria-hidden className="h-5 w-5" />
        </IconButton>
      )}
    >
      {(close) => <ThemeChoices close={close} />}
    </Popover>
  );
}

function ThemeChoices({ close }: { close: () => void }) {
  const { choice, setChoice } = useTheme();
  return (
    <>
      {(
        [
          ['light', 'Light', Sun],
          ['dark', 'Dark', Moon],
          ['system', 'Match device', Monitor],
        ] as const
      ).map(([v, l, I]) => (
        <MenuItem
          key={v}
          icon={<I />}
          onClick={() => {
            setChoice(v);
            close();
          }}
        >
          <span className="flex justify-between">
            {l}
            {choice === v && <Check aria-hidden className="h-4 w-4 text-brand" />}
          </span>
        </MenuItem>
      ))}
    </>
  );
}

function UserMenu() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;
  const initials = user.name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('') || '?';
  return (
    <Popover
      align="right"
      label="Account"
      trigger={({ open, toggle, id }) => (
        <button type="button" aria-haspopup="menu" aria-expanded={open} aria-controls={id} onClick={toggle} aria-label={`Account: ${user.name}`} className="ml-1 grid h-11 w-11 place-items-center rounded-full">
          <span className="grid h-9 w-9 place-items-center rounded-full bg-brand text-sm font-bold text-brand-ink">{initials}</span>
        </button>
      )}
    >
      {(close) => (
        <div className="w-64">
          <div className="border-b border-line px-2.5 pb-2.5 pt-2">
            <p className="truncate font-semibold">{user.name}</p>
            <p className="truncate text-xs text-muted">{user.email}</p>
            <p className="mt-1 text-xs text-muted">
              {user.roleName || 'No role'}
              {user.org && <> in {user.org.name}</>}
            </p>
          </div>
          <div className="pt-1">
            <MenuItem
              icon={<UserRound />}
              onClick={() => {
                navigate('/account');
                close();
              }}
            >
              Account and security
            </MenuItem>
            <MenuItem
              icon={<KeyRound />}
              onClick={() => {
                navigate('/account#password');
                close();
              }}
            >
              Change password
            </MenuItem>
            <div className="sm:hidden">
              <p className="px-2.5 pb-0.5 pt-2 text-xs text-muted">Theme</p>
              <ThemeChoices close={close} />
            </div>
            <MenuItem icon={<LogOut />} onClick={() => void signOut()}>
              Sign out
            </MenuItem>
          </div>
        </div>
      )}
    </Popover>
  );
}
